import { useQueryClient } from "@tanstack/react-query";
import { useCallback } from "react";
import { useTranslation } from "react-i18next";
import { SnackbarActionButton } from "../../../../../components/UIToolkit/Popups/SnackbarActionButton";
import { useAnalytics } from "../../../../../contexts/analytics/hooks";
import { useHospitalData } from "../../../../../contexts/HospitalDataProvider";
import { useSnackbar } from "../../../../../contexts/SnackbarContext";
import { nurseJobNotificationsQueryKey, useSaveNurseJobNotifications } from "../../../../../hooks/useNurseJobNotifications";
import type { NurseJobNotificationsResponse } from "../../../../../utils/jobNotificationTypes";
import { ChangesSource } from "../../../../../utils/models";

type Params = { departmentId: string; nurseId: number; firstName: string };

type SaveArgs = { jobId: string; jobName: string; isSilent: boolean };

/**
 * View-mode chip toggle. A click saves right away and the success toast offers Undo.
 * A failed save puts the chip back, so it never shows a value the server doesn't have.
 */
export const useJobNotificationChipToggle = ({ departmentId, nurseId, firstName }: Params) => {
	const queryClient = useQueryClient();
	const { t } = useTranslation();
	const { showSnackbar } = useSnackbar();
	const { showErrorSnackbar } = useHospitalData();
	const { track } = useAnalytics();
	const { mutateAsync } = useSaveNurseJobNotifications(departmentId);

	const save = useCallback(
		async ({ jobId, jobName, isSilent }: SaveArgs): Promise<boolean> => {
			const queryKey = nurseJobNotificationsQueryKey(departmentId, nurseId);
			await queryClient.cancelQueries({ queryKey });
			const previousEntry = queryClient.getQueryData<NurseJobNotificationsResponse>(queryKey)?.jobs.find((job) => job.jobId === jobId);
			queryClient.setQueryData<NurseJobNotificationsResponse>(
				queryKey,
				(current) =>
					current && {
						jobs: current.jobs.map((job) => (job.jobId === jobId ? { ...job, isSilent, isDefault: false, updatedSource: ChangesSource.Profile } : job)),
					},
			);
			const analytics = { nurse_id: nurseId, job_id: jobId, is_silent: isSilent };
			track("profile_job_notification_save_start", analytics);
			try {
				await mutateAsync({ nurseId, changes: [{ jobId, isSilent }] });
				track("profile_job_notification_save_success", analytics);
				return true;
			} catch (error) {
				// Restore only this job, so a save on another chip in the meantime isn't lost.
				if (previousEntry) {
					queryClient.setQueryData<NurseJobNotificationsResponse>(
						queryKey,
						(current) => current && { jobs: current.jobs.map((job) => (job.jobId === jobId ? previousEntry : job)) },
					);
				} else {
					await queryClient.invalidateQueries({ queryKey });
				}
				track("profile_job_notification_save_error", { ...analytics, error_message: error instanceof Error ? error.message : String(error) });
				showErrorSnackbar(t("jobNotifications.profile.saveFailed", { job: jobName }), analytics, error);
				return false;
			}
		},
		[departmentId, nurseId, queryClient, mutateAsync, track, showErrorSnackbar, t],
	);

	return useCallback(
		async (jobId: string, jobName: string, currentlySilent: boolean) => {
			const isSilent = !currentlySilent;
			track("profile_job_notification_toggle", { nurse_id: nurseId, job_id: jobId, is_silent: isSilent, surface: "chip" });
			if (!(await save({ jobId, jobName, isSilent }))) return;
			const undo = () => {
				track("profile_job_notification_undo_click", { nurse_id: nurseId, job_id: jobId, is_silent: currentlySilent });
				void save({ jobId, jobName, isSilent: currentlySilent });
			};
			showSnackbar(
				t(isSilent ? "jobNotifications.profile.toast.muted" : "jobNotifications.profile.toast.on", { job: jobName, name: firstName }),
				"success",
				undefined,
				<SnackbarActionButton label={t("undo")} onClick={undo} singleUse dataTestId={`profile-job-notification-undo-${jobId}`} />,
			);
		},
		[save, showSnackbar, t, track, nurseId, firstName],
	);
};
